/**
 * Utility function for smooth scrolling to elements
 * Works in browsers that don't support scroll-behavior: smooth
 */

export const smoothScrollTo = (elementId: string): void => {
  const element = document.getElementById(elementId);

  if (!element) return;

  // Check if browser supports smooth scrolling natively
  if ('scrollBehavior' in document.documentElement.style) {
    // Use native scrolling
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }

  // Fallback for browsers that don't support smooth scrolling
  const targetPosition = element.getBoundingClientRect().top + window.pageYOffset;
  const headerOffset = window.innerWidth <= 767 ? 60 : 80; // Match the header height
  const offsetPosition = targetPosition - headerOffset;

  // Animation parameters
  const duration = 1000;
  const startPosition = window.pageYOffset;
  const distance = offsetPosition - startPosition;
  let startTimestamp: number | null = null;

  // Easing function - easeInOutCubic
  const easing = (t: number): number => {
    return t < 0.5
      ? 4 * t * t * t
      : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
  };

  // Animation step function
  const step = (timestamp: number) => {
    if (!startTimestamp) startTimestamp = timestamp;

    const elapsed = timestamp - startTimestamp;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);

    window.scrollTo(0, startPosition + distance * easedProgress);

    if (elapsed < duration) {
      window.requestAnimationFrame(step);
    }
  };

  // Start animation
  window.requestAnimationFrame(step);
};

/**
 * Initialize smooth scrolling for all anchor links that point to IDs
 */
export const initSmoothScroll = (): void => {
  document.addEventListener('click', (e) => {
    // Check if clicked element is an anchor with hash
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');

    if (!anchor) return;

    const href = anchor.getAttribute('href');

    // Only handle anchors that link to IDs on the same page
    if (!href || !href.startsWith('#')) return;

    const targetId = href.substring(1);

    if (targetId) {
      e.preventDefault();
      smoothScrollTo(targetId);

      // Update URL without scrolling
      window.history.pushState(null, '', href);
    }
  });
}; 